import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import React, { useState } from "react";

const FAQ = () => {
	const [openIndex, setOpenIndex] = useState(null);

	const faqs = [
		{
			question: "What is Milburn?",
			answer: "Milburn is an all-in-one platform that combines AI-powered journaling and therapy to support your mental well-being. It provides a safe and personalized space to express your thoughts, feelings, and experiences, while receiving tailored advice and support.",
		},
		{
			question: "How is my data handled?",
			answer: "We take your privacy <strong>very seriously</strong>. All your conversations and journal entries are securely end to end encrypted with external key management. What does that practically mean? We can neither see nor share your information. Even if our primary database were to be hacked, the hackers would not be able to read your data. Your data is not used for training any AI models.",
		},
		{
			question: "What are the benefits of therapy and journaling?",
			answer: "Research has shown that <a href='https://www.aft.org/news/12-rewarding-benefits-therapy' target='_blank' class='text-blue-500 hover:underline'>therapeutic talking</a> and <a href='https://www.urmc.rochester.edu/Encyclopedia/content.aspx?ContentTypeID=1&ContentID=4552#:~:text=Journaling%20helps%20control%20your%20symptoms,and%20identifying%20negative%20thoughts%20and' target='_blank' class='text-blue-500 hover:underline'>reflective journaling</a> can significantly improve mental well-being. These practices help reduce stress and anxiety, increase self-awareness, process emotions more effectively, and provide clarity in decision-making. Regular journaling can also help identify patterns in thoughts and behaviors, while therapeutic dialogue offers new perspectives and coping strategies for life's challenges.",
		},
		{
			question: "How many sessions and journal entries are included?",
			answer: "We offer all new users 2 complimentary therapy sessions and 2 journal entries to experience Milburn. After that, you can start a subscription which includes unlimited therapy sessions and journal entries. The subscription begins with a one-week free trial, giving you even more time to explore all our features.",
		},
		{
			question: "I have more questions!",
			answer: "We'd love to help! You can reach out to us at <a href='mailto:help@milburnai.com' class='text-blue-500 hover:underline'>help@milburnai.com</a>. We typically respond within 24 hours.",
		},
	];

	const toggleQuestion = (index) => {
		setOpenIndex(openIndex === index ? null : index);
	};

	return (
		<div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
			<div className="w-full max-w-6xl backdrop-blur-lg rounded-3xl shadow-xl overflow-auto bg-white bg-opacity-90">
				<div className="py-10 md:py-16 px-6 md:px-10">
					<div className="flex justify-center items-center mb-10 md:mb-12 gap-3">
						<div className="w-11 mr-0 md:mr-3">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 200 250"
								className="w-full">
								<rect
									x="90"
									y="170"
									width="20"
									height="80"
									fill="#1e3a8a"
								/>
								<polygon
									points="10,170 190,170 100,50"
									fill="#3b82f6"
								/>
								<polygon
									points="20,120 180,120 100,20"
									fill="#60a5fa"
								/>
								<polygon
									points="30,70 170,70 100,0"
									fill="#93c5fd"
								/>
							</svg>
						</div>
						<h1 className="text-xl md:text-3xl font-light text-gray-800 text-center">
							Frequently Asked Questions
						</h1>
					</div>

					<div className="space-y-4">
						{faqs.map((faq, index) => (
							<div
								key={index}
								className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
								<button
									className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all duration-300"
									onClick={() => toggleQuestion(index)}>
									<span className="text-gray-800 font-light">
										{faq.question}
									</span>
									{openIndex === index ? (
										<ChevronUp
											className="text-blue-400"
											size={20}
										/>
									) : (
										<ChevronDown
											className="text-blue-400"
											size={20}
										/>
									)}
								</button>
								<AnimatePresence>
									{openIndex === index && (
										<motion.div
											initial={{ height: 0, opacity: 0 }}
											animate={{
												height: "auto",
												opacity: 1,
											}}
											exit={{ height: 0, opacity: 0 }}
											transition={{
												duration: 0.3,
												ease: "easeInOut",
											}}
											className="overflow-hidden">
											<div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
												<p
													className="text-gray-600 font-light"
													dangerouslySetInnerHTML={{
														__html: faq.answer,
													}}
												/>
											</div>
										</motion.div>
									)}
								</AnimatePresence>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

export default FAQ;
